// apps/web/src/components/finance/InvoiceManager.tsx
// Invoice management system

import type React from 'react';
import { useState, useEffect } from 'react';
import type { Invoice } from '../../types/invoice';
import { apiClient } from '../../lib/apiClient';

export function InvoiceManager() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/finance/invoices');
        setInvoices(response.data.invoices);
      } catch (err) {
        setError('Failed to load invoices');
        console.error('Error loading invoices:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  const handleCreateInvoice = async (invoiceData: any) => {
    try {
      const response = await apiClient.post('/api/v1/finance/invoices', invoiceData);
      setInvoices([response.data, ...invoices]);
      setShowCreateForm(false);
    } catch (err) {
      setError('Failed to create invoice');
      console.error('Error creating invoice:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading invoices...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="invoice-manager">
      <div className="header">
        <h2>Invoice Management</h2>
        <button 
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="btn btn-primary"
        >
          {showCreateForm ? 'Cancel' : 'Create Invoice'}
        </button>
      </div>

      {showCreateForm && (
        <InvoiceForm 
          onSubmit={handleCreateInvoice} 
          onCancel={() => setShowCreateForm(false)} 
        />
      )}

      <div className="invoices-list">
        {invoices.map(invoice => (
          <InvoiceCard key={invoice.id} invoice={invoice} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function InvoiceCard({ invoice }: { invoice: Invoice }) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'status-draft';
      case 'SENT': return 'status-sent';
      case 'PAID': return 'status-paid';
      case 'OVERDUE': return 'status-overdue';
      case 'CANCELLED': return 'status-cancelled';
      default: return 'status-default';
    }
  };

  return (
    <div className="invoice-card">
      <div className="invoice-header">
        <h3>INV-{invoice.id.substring(0, 6)}</h3>
        <span className={`status-badge ${getStatusColor(invoice.status)}`}>
          {invoice.status}
        </span>
      </div>
      <p><strong>Amount:</strong> ${invoice.amount.toLocaleString()}</p>
      <p><strong>Due:</strong> {invoice.dueDate}</p>
      <p><strong>Client:</strong> {invoice.clientId.substring(0, 8)}...</p>
      <p><strong>Description:</strong> {invoice.description || 'No description'}</p>
      <div className="actions">
        {invoice.status === 'DRAFT' && (
          <button className="btn btn-success">Send Invoice</button>
        )}
        {invoice.status === 'SENT' && (
          <button className="btn btn-primary">Mark Paid</button>
        )}
        <button className="btn btn-secondary">View</button>
        <button className="btn btn-outline">Download PDF</button>
      </div>
    </div>
  );
}

// Helper component
function InvoiceForm({ onSubmit, onCancel }: { onSubmit: any; onCancel: any }) {
  const [formData, setFormData] = useState({
    clientId: '',
    amount: '',
    currency: 'USD',
    dueDate: '',
    description: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="invoice-form">
      <h3>Create New Invoice</h3>
      <input
        type="text"
        placeholder="Client ID"
        value={formData.clientId}
        onChange={(e) => setFormData({...formData, clientId: e.target.value})}
        required
      />
      <input
        type="number"
        placeholder="Amount"
        value={formData.amount}
        onChange={(e) => setFormData({...formData, amount: e.target.value})}
        required
      />
      <input
        type="date"
        value={formData.dueDate}
        onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
        required
      />
      <textarea
        placeholder="Description"
        value={formData.description}
        onChange={(e) => setFormData({...formData, description: e.target.value})}
      />
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Create Invoice</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}